import { useCallback, useMemo, useRef } from "react";

/**
 * Zero-config anti-spam for the lead forms (Phase 35 — replaces reCAPTCHA).
 *
 * Nothing is loaded from a third party and there is no key to configure.
 * The backend (app/antispam.py) runs three checks: the hidden `website`
 * honeypot each form already carries, a fill-time floor, and a per-IP
 * rate limit. This module supplies the fill-time half:
 *
 *   - `useFormTimer()` records when the form mounted.
 *   - `timer.fields()` returns `{ form_started_at }` to spread into the
 *     POST body.
 *   - `await timer.settle()` waits out any remainder of `MIN_FILL_MS`
 *     before the request goes out, so a genuinely quick human (or an
 *     automated QA run) never trips the backend's `too_fast` answer —
 *     only scripts that hit the API directly do.
 *
 * Keep `MIN_FILL_MS` in sync with the backend constant.
 */
export const MIN_FILL_MS = 2000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Build a timer object from a start timestamp. Exposed so a parent that
 * mounted long before the form (e.g. a plan table opening a modal) can
 * hand its own start time down instead of the modal's.
 */
export const makeFormTimer = (startedAt) => {
  const started = Number.isFinite(startedAt) ? startedAt : Date.now();
  return {
    startedAt: started,
    fields: () => ({ form_started_at: started }),
    settle: async () => {
      const remaining = MIN_FILL_MS - (Date.now() - started) + 50;
      if (remaining > 0) await wait(remaining);
    },
  };
};

/** Stable per-mount form timer. */
export const useFormTimer = () => {
  const startRef = useRef(null);
  if (startRef.current === null) startRef.current = Date.now();
  const startedAt = startRef.current;
  const settle = useCallback(() => makeFormTimer(startedAt).settle(), [startedAt]);
  const fields = useCallback(() => ({ form_started_at: startedAt }), [startedAt]);
  return useMemo(() => ({ startedAt, settle, fields }), [startedAt, settle, fields]);
};

/**
 * Map a backend `detail` string to a user-facing message, or `null` when
 * the error isn't one of ours.
 */
export const antispamErrorMessage = (detail) => {
  if (detail === "too_fast") {
    return "That was quick! Please check your details and press submit again.";
  }
  if (detail === "rate_limited") {
    return "We've had a lot of requests from your connection. Please wait a few minutes and try again, or call us.";
  }
  return null;
};
